/**
 * Admin Service Interface for Agent Architect
 * Handles admin operations for users, organizations, agent prompts, and monitoring
 */

// User Interface
export interface User {
  uid: string;
  email: string;
  display_name?: string;
  systemRole?: string;
  is_active?: boolean;
  organization_id?: string;
  created_at?: string;
  updated_at?: string;
  last_login?: string;
}

// Request types for User operations
export interface CreateUserRequest {
  email: string;
  display_name?: string;
  systemRole: string;
  organization_id?: string;
}

export interface UpdateUserRequest {
  display_name?: string;
  systemRole?: string;
  is_active?: boolean;
  organization_id?: string;
}

// Organization Interface
export type SubscriptionTier =
  | 'FREE'
  | 'STARTER'
  | 'PROFESSIONAL'
  | 'ENTERPRISE';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  subscription_tier: SubscriptionTier;
  owner_uid: string;
  admin_uids: string[];
  max_users: number;
  max_projects: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Request types for Organization operations
export interface CreateOrganizationRequest {
  name: string;
  description?: string;
}

export interface UpdateOrganizationRequest {
  name?: string;
  description?: string;
  subscription_tier?: SubscriptionTier;
  max_users?: number;
  max_projects?: number;
  is_active?: boolean;
}

// Agent Prompt Interface

/**
 * AI Model configuration for agent prompts.
 * Task 070a: Added typed interface for model config editing in admin UI.
 */
export interface AIModelConfig {
  model?: string;
  temperature?: number;
  max_output_tokens?: number;
}

/**
 * Available models for agent configuration
 * Organized by generation: Gemini 3.0 (Preview), 2.5 (GA), 2.0 (GA)
 */
export const AVAILABLE_MODELS = [
  // Gemini 3.0 (Preview) - Latest reasoning-first models
  { value: 'gemini-3-pro', label: 'Gemini 3 Pro (Preview) - Most Capable' },
  {
    value: 'gemini-3-flash',
    label: 'Gemini 3 Flash (Preview) - Fast Reasoning',
  },
  // Gemini 2.5 (GA) - Production-ready capable models
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro - Advanced Reasoning' },
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash - Balanced' },
  {
    value: 'gemini-2.5-flash-lite',
    label: 'Gemini 2.5 Flash Lite - High Throughput',
  },
  // Gemini 2.0 (GA) - Cost-effective models
  { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash - Fast & Efficient' },
  {
    value: 'gemini-2.0-flash-lite',
    label: 'Gemini 2.0 Flash Lite - Ultra Fast',
  },
] as const;

export interface AgentPromptVersion {
  version: string;
  prompt_template: string;
  description: string;
  is_active: boolean;
  performance_notes?: string;
  created_at: string;
  created_by: string;
}

export interface AgentPrompt {
  prompt_id: string;
  agent_name: string;
  agent_function: string;
  current_version: string;
  versions: AgentPromptVersion[];
  title: string;
  description: string;
  category: string;
  placeholders: string[];
  ai_model_config: AIModelConfig;
  is_enabled: boolean;
  usage_count: number;
  last_used_at?: string;
  created_at: string;
  updated_at: string;
}

// Request types for Agent Prompt operations
export interface UpdateAgentPromptRequest {
  title?: string;
  description?: string;
  is_enabled?: boolean;
  ai_model_config?: AIModelConfig;
}

export interface AddPromptVersionRequest {
  prompt_template: string;
  description: string;
  performance_notes?: string;
}

// Sync from code response
export interface SyncAgentChange {
  agent_name: string;
  has_changes: boolean;
  current_version?: string;
  new_version?: string;
  change_summary?: string;
  sections_changed?: string[];
}

export interface SyncFromCodeResponse {
  preview_mode: boolean;
  total_agents: number;
  agents_with_changes: number;
  agents_synced: number;
  changes: SyncAgentChange[];
  sync_timestamp?: string;
  message: string;
}

// Analytics Interfaces
export interface StepMetrics {
  step_name: string;
  avg_duration_ms: number;
  success_rate: number;
  total_invocations: number;
}

export interface PerformanceMetrics {
  avg_response_time_ms: number;
  error_rate_percent: number;
  requests_last_24h: number;
  requests_last_7d: number;
  errors_last_24h: number;
  agent_step_metrics: Record<string, StepMetrics>;
}

export interface UsageMetrics {
  total_projects: number;
  projects_created_today: number;
  projects_created_this_week: number;
  projects_created_this_month: number;
  projects_by_status: Record<string, number>;
  total_users: number;
  active_users_today: number;
  active_users_this_week: number;
  users_by_role: Record<string, number>;
  total_jobs: number;
  jobs_by_status: Record<string, number>;
  jobs_created_today: number;
}

// Health Summary for Admin Console Heads-Up Display (Task 060a)
export type SystemStatus = 'operational' | 'degraded' | 'outage';

export interface HealthMetrics {
  active_users_24h: number;
  projects_created_24h: number;
  agent_invocations_24h: number;
  error_rate_1h: number;
}

export interface GCPDashboardUrls {
  main_dashboard: string;
  logs: string;
  errors: string;
  traces: string;
}

export interface HealthSummary {
  status: SystemStatus;
  last_updated: string;
  metrics: HealthMetrics;
  gcp_dashboard_urls: GCPDashboardUrls;
}

// Cost Monitoring Interfaces (Task 080)
export type CostPeriod = 'day' | 'week' | 'month';

export interface CostMetrics {
  total_cost_usd: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_requests: number;
  cost_by_step: Record<string, number>;
  cost_by_user: Record<string, number>;
  cost_by_organization: Record<string, number>;
  avg_cost_per_request: number;
  avg_cost_per_user: number;
  period: string;
  period_start: string;
  period_end: string;
}

export interface CostSummary {
  metrics: CostMetrics;
  previous_period_cost: number | null;
  cost_change_percent: number | null;
}

export interface CostTrend {
  date: string;
  cost_usd: number;
  requests: number;
  input_tokens: number;
  output_tokens: number;
}

export interface CostTrendsResponse {
  trends: CostTrend[];
  total_cost_usd: number;
  avg_daily_cost: number;
  projected_monthly_cost: number | null;
}

export interface TopCostUser {
  user_id: string;
  email: string | null;
  organization_id: string | null;
  organization_name: string | null;
  total_cost_usd: number;
  total_requests: number;
  total_tokens: number;
}

export interface TopCostUsersResponse {
  users: TopCostUser[];
  period: string;
  total_users: number;
}

// Infrastructure Cost Monitoring Interfaces (Task 081)
export interface GCPServiceCost {
  service_description: string;
  total_cost_usd: number;
  usage_amount: number;
  usage_unit: string | null;
}

export interface InfrastructureCostMetrics {
  total_cost_usd: number;
  cost_by_service: Record<string, number>;
  top_services: GCPServiceCost[];
  period: string;
  period_start: string;
  period_end: string;
  data_freshness: string;
}

export interface InfrastructureCostSummary {
  metrics: InfrastructureCostMetrics;
  previous_period_cost: number | null;
  cost_change_percent: number | null;
  billing_data_delay_hours: number;
}

export interface InfrastructureCostTrend {
  date: string;
  total_cost_usd: number;
  cloud_run_cost: number;
  firestore_cost: number;
  storage_cost: number;
  other_cost: number;
}

export interface InfrastructureCostTrendsResponse {
  trends: InfrastructureCostTrend[];
  total_cost_usd: number;
  avg_daily_cost: number;
  projected_monthly_cost: number | null;
}

export interface CombinedCostMetrics {
  api_cost_usd: number;
  infrastructure_cost_usd: number;
  total_platform_cost_usd: number;
  total_requests: number;
  per_request_api_cost: number;
  per_request_infra_cost: number;
  true_per_request_cost: number;
  period: string;
  period_start: string;
  period_end: string;
}

export interface CombinedCostSummary {
  metrics: CombinedCostMetrics;
  api_breakdown: Record<string, number>;
  infra_breakdown: Record<string, number>;
}

/**
 * AdminService interface defines the contract for admin operations
 */
export interface AdminService {
  // User Management
  /**
   * Fetch all users from the backend (admin only)
   * @returns Promise<User[]> List of users with their system roles
   */
  listUsers(): Promise<User[]>;

  /**
   * Create a new user
   * @param data User data to create
   * @returns Promise<User> Created user
   */
  createUser(data: CreateUserRequest): Promise<User>;

  /**
   * Update an existing user
   * @param uid User ID to update
   * @param data Partial user data to update
   * @returns Promise<User> Updated user
   */
  updateUser(uid: string, data: UpdateUserRequest): Promise<User>;

  /**
   * Delete (deactivate) a user
   * @param uid User ID to delete
   * @returns Promise<void>
   */
  deleteUser(uid: string): Promise<void>;

  // Organization Management
  /**
   * Fetch all organizations
   * @returns Promise<Organization[]> List of organizations
   */
  listOrganizations(): Promise<Organization[]>;

  /**
   * Get a specific organization
   * @param orgId Organization ID
   * @returns Promise<Organization> Organization details
   */
  getOrganization(orgId: string): Promise<Organization>;

  /**
   * Create a new organization
   * @param data Organization data to create
   * @returns Promise<Organization> Created organization
   */
  createOrganization(data: CreateOrganizationRequest): Promise<Organization>;

  /**
   * Update an existing organization
   * @param orgId Organization ID to update
   * @param data Partial organization data to update
   * @returns Promise<Organization> Updated organization
   */
  updateOrganization(
    orgId: string,
    data: UpdateOrganizationRequest
  ): Promise<Organization>;

  /**
   * Delete an organization
   * @param orgId Organization ID to delete
   * @returns Promise<void>
   */
  deleteOrganization(orgId: string): Promise<void>;

  /**
   * Get users in an organization
   * @param orgId Organization ID
   * @returns Promise<User[]> List of users in the organization
   */
  getOrganizationUsers(orgId: string): Promise<User[]>;

  /**
   * Add a user to an organization
   * @param orgId Organization ID
   * @param uid User ID to add
   * @returns Promise<void>
   */
  addUserToOrganization(orgId: string, uid: string): Promise<void>;

  /**
   * Remove a user from an organization
   * @param orgId Organization ID
   * @param uid User ID to remove
   * @returns Promise<void>
   */
  removeUserFromOrganization(orgId: string, uid: string): Promise<void>;

  // Agent Prompt Management
  /**
   * Fetch all agent prompts
   * @returns Promise<AgentPrompt[]> List of agent prompts
   */
  listAgentPrompts(): Promise<AgentPrompt[]>;

  /**
   * Get a specific agent prompt
   * @param agentName Agent name (discovery, design, etc.)
   * @returns Promise<AgentPrompt> Agent prompt details
   */
  getAgentPrompt(agentName: string): Promise<AgentPrompt>;

  /**
   * Update an agent prompt
   * @param agentName Agent name
   * @param data Partial prompt data to update
   * @returns Promise<AgentPrompt> Updated prompt
   */
  updateAgentPrompt(
    agentName: string,
    data: UpdateAgentPromptRequest
  ): Promise<AgentPrompt>;

  /**
   * Add a new version to an agent prompt
   * @param agentName Agent name
   * @param data New version data
   * @returns Promise<AgentPrompt> Updated prompt with new version
   */
  addAgentPromptVersion(
    agentName: string,
    data: AddPromptVersionRequest
  ): Promise<AgentPrompt>;

  /**
   * Rollback an agent prompt to a previous version
   * @param agentName Agent name
   * @param version Version to rollback to
   * @returns Promise<AgentPrompt> Prompt with rolled back version
   */
  rollbackAgentPromptVersion(
    agentName: string,
    version: string
  ): Promise<AgentPrompt>;

  /**
   * Sync agent prompts from code to Firestore
   * @param preview If true, only show what would change without applying
   * @returns Promise<SyncFromCodeResponse> Sync results
   */
  syncAgentPromptsFromCode(preview?: boolean): Promise<SyncFromCodeResponse>;

  // Analytics & Monitoring
  /**
   * Get usage metrics
   * @param refresh Whether to bypass cache
   * @returns Promise<UsageMetrics> Usage metrics
   */
  getUsageMetrics(refresh?: boolean): Promise<UsageMetrics>;

  /**
   * Get performance metrics
   * @returns Promise<PerformanceMetrics> Performance metrics
   */
  getPerformanceMetrics(): Promise<PerformanceMetrics>;

  /**
   * Get health summary for Admin Console heads-up display (Task 060a)
   * @returns Promise<HealthSummary> Health summary with status, metrics, and GCP dashboard links
   */
  getHealthSummary(): Promise<HealthSummary>;

  // Cost Monitoring (Task 080)
  /**
   * Get platform cost summary for a time period
   * @param period Time period: day, week, or month
   * @param orgId Optional organization ID filter
   * @returns Promise<CostSummary> Cost metrics with period comparison
   */
  getCostSummary(period?: CostPeriod, orgId?: string): Promise<CostSummary>;

  /**
   * Get cost trends over time
   * @param days Number of days to include (1-90)
   * @returns Promise<CostTrendsResponse> Daily cost data points
   */
  getCostTrends(days?: number): Promise<CostTrendsResponse>;

  /**
   * Get top users by cost
   * @param limit Maximum users to return (1-100)
   * @param period Time period: day, week, or month
   * @returns Promise<TopCostUsersResponse> Ranked list of users
   */
  getTopCostUsers(
    limit?: number,
    period?: CostPeriod
  ): Promise<TopCostUsersResponse>;

  /**
   * Get costs by workflow step
   * @param period Time period: day, week, or month
   * @returns Promise<Record<string, number>> Cost per step
   */
  getCostsByStep(period?: CostPeriod): Promise<Record<string, number>>;

  /**
   * Get costs by organization
   * @param period Time period: day, week, or month
   * @returns Promise<Record<string, number>> Cost per organization
   */
  getCostsByOrganization(period?: CostPeriod): Promise<Record<string, number>>;

  // Infrastructure Cost Monitoring (Task 081)
  /**
   * Get GCP infrastructure cost summary
   * @param period Time period: day, week, or month
   * @returns Promise<InfrastructureCostSummary> Infrastructure costs from billing export
   */
  getInfrastructureCostSummary(
    period?: CostPeriod
  ): Promise<InfrastructureCostSummary>;

  /**
   * Get infrastructure cost trends over time
   * @param days Number of days to include (1-90)
   * @returns Promise<InfrastructureCostTrendsResponse> Daily infrastructure cost data
   */
  getInfrastructureCostTrends(
    days?: number
  ): Promise<InfrastructureCostTrendsResponse>;

  /**
   * Get combined API + infrastructure cost summary
   * @param period Time period: day, week, or month
   * @returns Promise<CombinedCostSummary> Combined costs with true per-request cost
   */
  getCombinedCostSummary(period?: CostPeriod): Promise<CombinedCostSummary>;
}
